import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { InlineDocsHint, SectionLabel } from '../layout/Typography';

export interface FieldGroupProps {
  /** Caption shown above the control. */
  label: ReactNode;
  /** Optional leading icon rendered before the label text. */
  icon?: ReactNode;
  /** Optional helper text rendered on a single muted line below the control. */
  hint?: ReactNode;
  /**
   * Optional docs slug (with optional `#anchor`) appended to the end of the
   * label row as an `InlineDocsHint`. Omitted → no docs affordance. Renders
   * nothing when no docs renderer is registered (stories/tests, docs disabled).
   */
  docsSlug?: string;
  /** Extra classes on the outer wrapper (e.g. `min-w-0` for flex-row chip rows). */
  className?: string;
  /** Extra classes on the label row. */
  labelClassName?: string;
  children: ReactNode;
}

/**
 * A single labelled field block standardized on the `SectionLabel` micro-label
 * (uppercase, `text-2xs`, strong tone) above its control, with an optional
 * leading icon, a trailing inline docs hint, and a single muted helper line
 * below. It is the dense, caption-style counterpart to `FormField`:
 *
 * - Reach for `FieldGroup` for config/chip fields and compact editors where a
 *   quiet uppercase caption matches the surrounding type scale (the blueprint
 *   create form, the launcher's Advanced chips via {@link ConfigChipField}).
 * - Reach for `FormField` for primary form inputs that want a full-size
 *   shadcn `<Label>` and a validation-error slot.
 *
 * Keeping every caption on this one component means the label orientation,
 * label→control gap, left inset, and hint styling stay identical across the
 * chip fields and full-width editors that sit next to each other in a form.
 */
export function FieldGroup({
  label,
  icon,
  hint,
  docsSlug,
  className,
  labelClassName,
  children,
}: FieldGroupProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <SectionLabel
        as="span"
        tone="strong"
        className={cn('flex items-center gap-1.5 pl-0.5', labelClassName)}
      >
        {icon}
        <span className="truncate">{label}</span>
        {docsSlug ? <InlineDocsHint slug={docsSlug} className="shrink-0 opacity-70" /> : null}
      </SectionLabel>
      {children}
      {hint ? <p className="pl-0.5 text-2xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
